/**
 * src/gm/platform/artifacts.ts — 产物区（替代线下 game/public/games/<project_id>/ 目录）。
 * 键约定：
 *   tp3:artifact:<project_id>:design-doc   策划设计文档（可选，供程序/评审注入）
 *   tp3:artifact:<project_id>:art-spec     美术视觉规格书
 *   tp3:artifact:<project_id>:assets-css   CSS 变量与类（可空）
 *   tp3:artifact:<project_id>:index-html   游戏本体
 *
 * 分层纪律（设计稿 §7）：platform 不 import events——审计与容量预警事件由调用方
 * （kernel）在写入后产出；本模块只返回事实。
 */

import {
  STORAGE_BUDGET_BYTES,
  STORAGE_WARN_RATIO,
  artifactKey,
  type ArtifactKind,
} from './config'
import { GmError } from './errors'
import { safeStorage } from './storage'

export type { ArtifactKind }

function storage(): Storage {
  const s = safeStorage()
  if (!s) {
    throw new GmError('SAVE_CORRUPT', '这台机器没有卡带槽（localStorage 不可用）。')
  }
  return s
}

export function readArtifact(projectId: string, kind: ArtifactKind): string | null {
  try {
    return safeStorage()?.getItem(artifactKey(projectId, kind)) ?? null
  } catch {
    return null
  }
}

export interface ArtifactWriteResult {
  /** 写入内容长度（字符数），供审计事件 */
  length: number
  /** 写入后总占用是否已超预警线 */
  nearCapacity: boolean
}

/** 写产物；超容抛 STORAGE_FULL 并引导导出/清理 */
export function writeArtifact(projectId: string, kind: ArtifactKind, content: string): ArtifactWriteResult {
  try {
    storage().setItem(artifactKey(projectId, kind), content)
  } catch (err) {
    throw new GmError(
      'STORAGE_FULL',
      '卡带架塞满了——去「电脑 → 系统设置」导出旧存档，或清理图鉴里的老作品再来。',
      err,
    )
  }
  return { length: content.length, nearCapacity: isNearCapacity() }
}

/** 删除某项目全部产物（放弃项目用；结算后保留——图鉴要玩） */
export function clearArtifacts(projectId: string): void {
  const kinds: ArtifactKind[] = ['design-doc', 'art-spec', 'assets-css', 'index-html', 'last-failure']
  for (const k of kinds) {
    try {
      safeStorage()?.removeItem(artifactKey(projectId, k))
    } catch {
      // 忽略
    }
  }
}

/** 删除单个产物（事故情报一次性消费等场景） */
export function removeArtifact(projectId: string, kind: ArtifactKind): void {
  try {
    safeStorage()?.removeItem(artifactKey(projectId, kind))
  } catch {
    // 忽略
  }
}

/** 估算 localStorage 总占用（字符数 ×2 粗算字节） */
export function estimateStorageBytes(): number {
  let total = 0
  const s = safeStorage()
  if (!s) return 0
  try {
    for (let i = 0; i < s.length; i++) {
      const k = s.key(i)
      if (k) total += (k.length + (s.getItem(k)?.length ?? 0)) * 2
    }
  } catch {
    // 忽略
  }
  return total
}

export function isNearCapacity(): boolean {
  return estimateStorageBytes() > STORAGE_BUDGET_BYTES * STORAGE_WARN_RATIO
}
